// File: src/components/Procurement/VendorPaymentModal.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const VendorPaymentModal = ({ open, onClose, onSaved }) => {
  const [vendors, setVendors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    vendor_id: '',
    invoice_id: '',
    amount_paid: '',
    payment_date: '',
    payment_method: '',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      axios.get('/solo-mdsh-api/vendors').then((res) => setVendors(res.data || []));
      axios.get('/solo-mdsh-api/invoices').then((res) => setInvoices(res.data || []));
    }
  }, [open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const { vendor_id, amount_paid, payment_date } = form;

    if (!vendor_id || !amount_paid || !payment_date) {
      setError('⚠️ Vendor, Amount Paid, and Date are required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        ...form,
        amount_paid: parseFloat(form.amount_paid),
      };

      await axios.post('/solo-mdsh-api/vendor-payments', payload);
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Payment save failed:', err);
      setError('❌ Failed to save payment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 text-white border border-white/10">
        <DialogHeader>
          <DialogTitle>Add Vendor Payment</DialogTitle>
        </DialogHeader>

        {error && <div className="text-red-400 mb-2 text-sm">{error}</div>}

        <div className="space-y-4">
          {/* Vendor Select */}
          <select
            className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700"
            value={form.vendor_id}
            onChange={(e) => handleChange('vendor_id', e.target.value)}
          >
            <option value="">Select Vendor</option>
            {vendors.map((v) => (
              <option key={v.vendor_id} value={v.vendor_id}>
                {v.vendor_name}
              </option>
            ))}
          </select>

          {/* Invoice Select */}
          <select
            className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700"
            value={form.invoice_id}
            onChange={(e) => handleChange('invoice_id', e.target.value)}
          >
            <option value="">Select Invoice (optional)</option>
            {invoices.map((inv) => (
              <option key={inv.invoice_id} value={inv.invoice_id}>
                #{inv.invoice_id} – {inv.vendor_name} – £{inv.amount_due}
              </option>
            ))}
          </select>

          <Input
            type="number"
            placeholder="Amount Paid"
            value={form.amount_paid}
            onChange={(e) => handleChange('amount_paid', e.target.value)}
          />

          <Input
            type="date"
            value={form.payment_date}
            onChange={(e) => handleChange('payment_date', e.target.value)}
          />

          {/* Payment Method */}
          <select
            className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700"
            value={form.payment_method}
            onChange={(e) => handleChange('payment_method', e.target.value)}
          >
            <option value="">Select Payment Method</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Cheque">Cheque</option>
            <option value="Cash">Cash</option>
          </select>

          <Textarea
            placeholder="Optional notes..."
            value={form.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
          />
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? 'Saving...' : 'Save Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VendorPaymentModal;
